import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { i18n } from '../../i18n/i18n';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import { ENROLLMENT_STATUSES } from '../../shared/types/common.model';
import '../icon/icon';
import '../tag/tag';

@customElement('luds-timeline')
export class LudsTimeline extends LitElement {
  static override styles = [
    overrideDesignTokens,
    ludsBaseFont,
    css`
      :host {
        /* Public variables */
        --luds-timeline-progress-color: var(--strokes-stroke-secondary);
        --luds-timeline-background-color: var(--colours-neutrals-white-negative);
        --luds-timeline-completed-color: var(--semantic-progress);
        --luds-timeline-failed-color: var(--semantic-alert);
        --luds-timeline-active-color: var(--semantic-colour-tokens-stroke-stroke-brand);
        --luds-timeline-content-spacing: var(--spacing-spacing-16);
        --luds-timeline-item-background-active: var(--semantic-colour-tokens-surface-surface-secondary, #f7f9fa);

        /* Private variables */
        --circle-size: 16px;
        --circle-half-size: calc(var(--circle-size) / 2);
      }

      .timeline-item {
        display: flex;
        align-items: center;
        position: relative;
        min-height: 56px;
        padding-left: var(--spacing-spacing-24);

        &.disabled {
          opacity: 0.5;
        }
      }

      .timeline-item::before {
        content: '';
        position: absolute;
        width: 2px;
        background-color: var(--luds-timeline-progress-color);
        z-index: 0;
        transform: translate(-1px, -100%);
        height: calc(100% - var(--circle-size) - 2px);
        bottom: 0;
      }

      .timeline-item.first::before,
      .timeline-item.no-sequence::before {
        display: none;
      }

      .timeline-item.completed::before {
        background-color: var(--luds-timeline-completed-color);
      }

      .circle-content {
        position: absolute;
        width: var(--circle-size);
        height: var(--circle-size);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background-color: var(--luds-timeline-background-color);
        border: 2px solid var(--luds-timeline-progress-color);
        z-index: 1;
        transform: translate(-50%, -50%);
        top: 50%;
      }

      .circle-content.completed {
        border-color: var(--luds-timeline-completed-color);
        background-color: var(--luds-timeline-completed-color);
      }

      .circle-content.failed {
        border-color: var(--luds-timeline-failed-color);
        background-color: var(--luds-timeline-failed-color);
      }

      .timeline-item.active {
        background-color: var(--luds-timeline-item-background-active);
        font-weight: 700;

        .circle-content.failed {
          background-color: var(--luds-timeline-item-background-active);
        }
      }

      .timeline-content {
        margin-left: calc(var(--spacing-spacing-12) + var(--circle-half-size));
      }

      .timeline-content-wrapper {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: var(--luds-timeline-content-spacing);
        width: 100%;
      }
    `,
  ];

  @property({ type: Number })
  status: ENROLLMENT_STATUSES = ENROLLMENT_STATUSES.IN_PROGRESS;

  @property({ type: String })
  icon = 'checkmark';

  @property({ type: String })
  customClass = '';

  @property({ type: Boolean })
  sequenced = true;

  @property({ type: Boolean })
  active = false;

  @property({ type: Boolean })
  disabled = false;

  @property({ type: String })
  timelineTitle = '';

  // Property used to trigger updates when translations change
  @property({ type: Number })
  timestamp?: number;

  override render() {
    const isPendingReview = this.status === ENROLLMENT_STATUSES.PENDING_REVIEW;
    const isCompleted = this.status === ENROLLMENT_STATUSES.COMPLETED;
    const isPassed = this.status === ENROLLMENT_STATUSES.PASSED;
    const isFailed = this.status === ENROLLMENT_STATUSES.FAILED;

    const timelineClasses = classMap({
      'timeline-item': true,
      active: this.active,
      completed: isCompleted || isPendingReview || isPassed,
      disabled: this.disabled,
      'no-sequence': !this.sequenced,
      [this.customClass]: this.customClass.length > 0,
    });

    const circleClasses = classMap({
      'circle-content': true,
      completed: isCompleted || isPendingReview || isPassed,
      failed: isFailed,
      active: this.active,
    });

    return html`
      <div class="${timelineClasses}" title="${this.timelineTitle}">
        <div class="${circleClasses}">
          <luds-icon
            title=${ENROLLMENT_STATUSES[`${this.status}`]}
            icon="${this.icon}"
            size="extra-small"
            color="white"></luds-icon>
        </div>
        <div class="timeline-content-wrapper">
          <div class="timeline-content">
            <slot></slot>
          </div>
          ${isPendingReview
            ? html` <luds-tag
                type="warning"
                size="small"
                content="${i18n('lua.enrollment_status.pending')}"></luds-tag>`
            : nothing}
        </div>
      </div>
    `;
  }
}
